/* START_MODULE_CONTRACT
 * PURPOSE: Мини-прогресс-бар загрузки проектной роли (0–100%).
 * SCOPE: RoleWorkloadBar, clampWorkloadPercent.
 * DEPENDS: MUI, appTheme transitions
 * LINKS: M-FE-ROLES, M-FE-EMPLOYEES
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * RoleWorkloadBar - визуализация процента загрузки роли.
 * clampWorkloadPercent - нормализация значения 0–100.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Высота шкалы уменьшена в 2 раза (26×10 / 36×12 / 48×16).
 * LAST_CHANGE: 2026-07-14 - Мини-прогресс-бар вместо текстового процента загрузки роли.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import { createInteractiveSurfaceTransition } from '../theme/appTheme';

const SIZE_PRESETS = {
  sm: { width: 26, height: 10 },
  md: { width: 36, height: 12 },
  lg: { width: 48, height: 16 },
};

/* START_METHOD_clampWorkloadPercent */
/* START_CONTRACT: clampWorkloadPercent
 * PURPOSE: Привести процент загрузки к диапазону 0–100.
 * INPUTS: { value: number|string|null|undefined }.
 * OUTPUTS: { number }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: clampWorkloadPercent
 */
export function clampWorkloadPercent(value) {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(num)));
}
/* END_METHOD_clampWorkloadPercent */

/* START_METHOD_RoleWorkloadBar */
/* START_CONTRACT: RoleWorkloadBar
 * PURPOSE: Отобразить компактную шкалу загрузки роли с подсказкой точного процента.
 * INPUTS: { value: number, accentColor?: string, size?: 'sm'|'md'|'lg', sx?: object }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ROLES, M-FE-EMPLOYEES
 * END_CONTRACT: RoleWorkloadBar
 */
export default function RoleWorkloadBar({ value, accentColor, size = 'sm', sx }) {
  const theme = useTheme();
  const percent = clampWorkloadPercent(value);
  const { width, height } = SIZE_PRESETS[size] ?? SIZE_PRESETS.sm;
  const fillColor = accentColor ?? theme.palette.primary.main;

  return (
    <Tooltip title={`Загрузка: ${percent}%`} placement="top" arrow>
      <Box
        component="span"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Загрузка ${percent} процентов`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          width,
          height,
          borderRadius: 999,
          bgcolor: alpha(fillColor, 0.12),
          boxShadow: `inset 0 0 0 1px ${alpha(fillColor, 0.16)}`,
          overflow: 'hidden',
          flexShrink: 0,
          ...sx,
        }}
      >
        <Box
          sx={{
            width: `${percent}%`,
            minWidth: percent > 0 ? 3 : 0,
            height: '100%',
            borderRadius: 999,
            bgcolor: fillColor,
            boxShadow: percent > 0 ? `0 0 6px ${alpha(fillColor, 0.35)}` : 'none',
            transition: createInteractiveSurfaceTransition(theme, ['width', 'background-color']),
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }}
        />
      </Box>
    </Tooltip>
  );
}
/* END_METHOD_RoleWorkloadBar */
